import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import API_BASE from './config';
import RegistrationForm from './components/RegistrationForm';
import AdminConfig from './components/AdminConfig';
import AdminLogin from './components/AdminLogin';
import UserManagement from './components/UserManagement';
import Dashboard from './components/Dashboard';
import CateringOverview from './components/CateringOverview';
import EditRegistration from './components/EditRegistration';
import VerifyRegistration from './components/VerifyRegistration';
import SignDelegation from './components/SignDelegation';
import HelpButton from './components/HelpButton';

// Public page: registration only
function PublicPage() {
  return (
    <div className="app">
      <header>
        <img src="/HEP2026_logo.jpeg" alt="School Party Logo" className="header-logo" />
        <h1>Festa di Fine Anno Scolastico</h1>
        <p>Sistema di Registrazione</p>
      </header>
      <div className="container">
        <RegistrationForm />
      </div>
      <HelpButton context="registration" />
    </div>
  );
}

// Admin area
function AdminArea() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [adminUser, setAdminUser] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const navigate = useNavigate();

  // Restore session on mount
  useEffect(() => {
    const saved = sessionStorage.getItem('adminSession');
    if (saved) {
      try {
        const session = JSON.parse(saved);
        // Validate the code is still valid
        fetch(`${API_BASE}/admin/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: session.code })
        }).then(res => {
          if (res.ok) {
            setAdminUser(session);
          } else {
            sessionStorage.removeItem('adminSession');
          }
          setSessionLoading(false);
        }).catch(() => {
          sessionStorage.removeItem('adminSession');
          setSessionLoading(false);
        });
      } catch {
        sessionStorage.removeItem('adminSession');
        setSessionLoading(false);
      }
    } else {
      setSessionLoading(false);
    }
  }, []);

  const handleLogin = (user) => {
    setAdminUser(user);
    sessionStorage.setItem('adminSession', JSON.stringify(user));
    setActiveTab('dashboard');
  };

  const handleLogout = () => {
    setAdminUser(null);
    sessionStorage.removeItem('adminSession');
    navigate('/');
  };

  const isOrganizer = adminUser && adminUser.role === 'organizer';

  if (sessionLoading) {
    return (
      <div className="app">
        <header>
          <img src="/HEP2026_logo.jpeg" alt="School Party Logo" className="header-logo" />
          <h1>Festa di Fine Anno Scolastico</h1>
          <p>Amministrazione</p>
        </header>
        <div className="container" style={{ textAlign: 'center', padding: '40px' }}>
          <p>Caricamento...</p>
        </div>
      </div>
    );
  }

  if (!adminUser) {
    return (
      <div className="app">
        <header>
          <img src="/HEP2026_logo.jpeg" alt="School Party Logo" className="header-logo" />
          <h1>Festa di Fine Anno Scolastico</h1>
          <p>Amministrazione</p>
        </header>
        <div className="container">
          <AdminLogin onLogin={handleLogin} />
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header>
        <img src="/HEP2026_logo.jpeg" alt="School Party Logo" className="header-logo" />
        <h1>Festa di Fine Anno Scolastico</h1>
        <p>Amministrazione</p>
        <div className="header-user">
          Connesso come: {adminUser.name} ({adminUser.role === 'organizer' ? 'Organizzatore' : `Rapp. - ${adminUser.class}`})
          <button className="btn-logout" onClick={handleLogout}>Esci</button>
        </div>
      </header>

      <div className="container">
        <div className="nav-tabs">
          <button
            className={`nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </button>
          {isOrganizer && (
            <>
              <button
                className={`nav-tab ${activeTab === 'catering' ? 'active' : ''}`}
                onClick={() => setActiveTab('catering')}
              >
                Catering
              </button>
              <button
                className={`nav-tab ${activeTab === 'config' ? 'active' : ''}`}
                onClick={() => setActiveTab('config')}
              >
                Configurazione
              </button>
              <button
                className={`nav-tab ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => setActiveTab('users')}
              >
                Codici di Accesso
              </button>
            </>
          )}
        </div>

        {activeTab === 'dashboard' && <Dashboard accessCode={adminUser.code} adminUser={adminUser} />}
        {activeTab === 'config' && isOrganizer && <AdminConfig accessCode={adminUser.code} />}
        {activeTab === 'catering' && isOrganizer && <CateringOverview accessCode={adminUser.code} />}
        {activeTab === 'users' && isOrganizer && <UserManagement accessCode={adminUser.code} />}
      </div>
      <HelpButton context="admin" adminRole={adminUser.role} />
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<PublicPage />} />
        <Route path="/admin" element={<AdminArea />} />
        <Route path="/edit/:token" element={
          <div className="app">
            <header>
              <img src="/HEP2026_logo.jpeg" alt="School Party Logo" className="header-logo" />
              <h1>Festa di Fine Anno Scolastico</h1>
              <p>Sistema di Registrazione</p>
            </header>
            <EditRegistration />
            <HelpButton context="registration" />
          </div>
        } />
        <Route path="/verify/:token" element={
          <div className="app">
            <header>
              <img src="/HEP2026_logo.jpeg" alt="School Party Logo" className="header-logo" />
              <h1>Festa HEP</h1>
              <p>Verifica Registrazione</p>
            </header>
            <VerifyRegistration />
            <HelpButton context="verify" />
          </div>
        } />
        <Route path="/sign-delegation/:token" element={
          <div className="app">
            <header>
              <img src="/HEP2026_logo.jpeg" alt="School Party Logo" className="header-logo" />
              <h1>Festa HEP</h1>
              <p>Firma Delega</p>
            </header>
            <SignDelegation />
          </div>
        } />
      </Routes>
    </Router>
  );
}

export default App;
